import React, { type FC, type KeyboardEvent, type PointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import { assert } from '../../../../utils'

import { ConnectwareError, ConnectwareErrorType, Translation } from '../../../../domain'

import { useTranslator } from '../../Internationalization'
import { iconButtonFocusRing } from '../../theme'
import { RESIZE_CURSOR, useDraggingBodyStyle } from '../Dragging'

/** In px */
const MIN_WIDTH = 560
/** Past this share of the outlet's width, letting go opens the page */
const SNAP_SHARE = 0.8
/** In px: the snap point is never nearer, so a narrow outlet still leaves room to resize */
const SNAP_MIN_WIDTH = 640
/** In px */
const KEY_STEP = 32
/** In px: a press moving less than this, as a double click's jitter does, is no drag yet */
const DRAG_DEAD_ZONE = 4

/** In px, centred on the panel's edge */
const HIT_WIDTH = 14
/** In px */
const BAR_WIDTH = 4
/** In px */
const BAR_HEIGHT = 40
/** In px */
const BAR_HOVER_HEIGHT = 80
/** The panel's body is a stacking context of its own at this level, so nothing in it rises above what's drawn over it */
export const BODY_Z_INDEX = 0
export const ABOVE_BODY_Z_INDEX = BODY_Z_INDEX + 1

/** A drag under way: the panel's width, or the outlet's while letting go would open the page */
export type PanelDrag = Readonly<{ width: number, snapping: boolean }>

const findSnapWidth = (outletWidth: number): number => Math.max(outletWidth * SNAP_SHARE, SNAP_MIN_WIDTH)

/** The widest the panel stays: up to the snap point when letting go there opens the page, else the outlet */
const findMaxWidth = (outletWidth: number, snaps: boolean): number => Math.floor(snaps ? Math.min(outletWidth, findSnapWidth(outletWidth)) : outletWidth)

const clampWidth = (width: number, maxWidth: number): number => Math.round(Math.min(maxWidth, Math.max(MIN_WIDTH, width)))

/** A width set earlier, kept within the bounds of the current outlet */
export const toPanelWidthStyle = (width: number, snaps: boolean): string => {
    const kept = `${Math.max(MIN_WIDTH, width)}px`
    return snaps ? `min(100%, ${kept}, max(${SNAP_SHARE * 100}%, ${SNAP_MIN_WIDTH}px))` : `min(100%, ${kept})`
}

const gripStyle = (dragging: boolean): SxProps<Theme> => (theme) => {
    const bar = (height: string, color: string): SxProps<Theme> => ({ height, backgroundColor: color })

    return {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `${-HIT_WIDTH / 2}px`,
        zIndex: ABOVE_BODY_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${HIT_WIDTH}px`,
        cursor: RESIZE_CURSOR,
        touchAction: 'none',
        '&::after': {
            content: '""',
            width: `${BAR_WIDTH}px`,
            borderRadius: `${BAR_WIDTH / 2}px`,
            transition: dragging ? 'none' : theme.transitions.create(['height', 'background-color'], { duration: theme.transitions.duration.shorter }),
            ...(dragging ? bar('100%', theme.palette.primary.main) : bar(`${BAR_HEIGHT}px`, theme.palette.grey[300])),
        },
        '&:hover::after': dragging ? {} : bar(`${BAR_HOVER_HEIGHT}px`, theme.palette.primary.main),
        '&:focus-visible': iconButtonFocusRing(theme),
        '@media (prefers-reduced-motion: reduce)': { '&::after': { transition: 'none' } },
    }
}

type Bounds = Readonly<{ width: number, outletWidth: number }>

/** `moved` stays `null` until the pointer has left the dead zone */
type Drag = Readonly<{ pointerId: number, startX: number, start: Bounds }> & { moved: PanelDrag | null }

const measure = (grip: HTMLElement | null): Bounds => {
    const panel = grip?.parentElement
    const outlet = panel?.parentElement
    assert(panel && outlet, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'A record panel grip must be inside its panel, inside its outlet'))
    return { width: panel.getBoundingClientRect().width, outletWidth: outlet.getBoundingClientRect().width }
}

type Props = Readonly<{
    /** The drag under way, as last reported through `onDrag` */
    drag: PanelDrag | null
    /** The panel's width style, to measure the panel again when it changes */
    widthStyle: string | undefined
    /** Letting go past the snap point calls it; without it, there's no snap point */
    onExpand: VoidFunction | undefined
    /** While dragging, `null` once the drag is over */
    onDrag: (drag: PanelDrag | null) => void
    /** `null` for the default width */
    onWidthChange: (width: number | null) => void
}>

/**
 * The panel's left edge, a vertical separator: dragging it, or ← and → (32px steps, ← widens), sets the panel's width, never under 560px
 * nor over the outlet; double-clicking it goes back to the default width.
 * With `onExpand`, letting go past `max(80% of the outlet, 640px)` expands instead, and the keys stop at that point.
 * The width is only committed when the pointer is released; a cancelled or interrupted drag leaves it as it was.
 * Only the primary pointer's main button drags, and only past a small dead zone, so a double click commits no drag.
 * Precedent: the table's column `ResizeGrip`
 */
export const PanelGrip: FC<Props> = ({ drag, widthStyle, onExpand, onDrag, onWidthChange }) => {
    const translator = useTranslator()
    const ref = useRef<HTMLDivElement>(null)
    const dragRef = useRef<Drag | null>(null)
    const [dragging, setDragging] = useState(false)
    const [bounds, setBounds] = useState<Bounds | null>(null)
    const snaps = onExpand !== undefined

    useDraggingBodyStyle(dragging)

    const remeasure = useCallback(() => {
        const next = measure(ref.current)
        setBounds((previous) => (previous?.width === next.width && previous.outletWidth === next.outletWidth ? previous : next))
    }, [])

    useLayoutEffect(remeasure, [remeasure, widthStyle])

    useEffect(() => {
        window.addEventListener('resize', remeasure)
        return () => window.removeEventListener('resize', remeasure)
    }, [remeasure])

    /** Unmounted mid-drag, the panel would otherwise keep the uncommitted width */
    useEffect(() => {
        if (!dragging) {
            return
        }

        return () => {
            if (dragRef.current) {
                onDrag(null)
            }
        }
    }, [dragging])

    const findDrag = (event: PointerEvent<HTMLElement>): Drag | null => (dragRef.current?.pointerId === event.pointerId ? dragRef.current : null)

    const end = (): void => {
        dragRef.current = null
        setDragging(false)
        onDrag(null)
    }

    const onPointerDown = (event: PointerEvent<HTMLElement>): void => {
        if (event.button !== 0 || !event.isPrimary || dragRef.current) {
            return
        }

        /** Keeps the press from selecting text or moving the focus */
        event.preventDefault()

        event.currentTarget.setPointerCapture(event.pointerId)
        dragRef.current = { pointerId: event.pointerId, startX: event.clientX, start: measure(ref.current), moved: null }
        setDragging(true)
    }

    const onPointerMove = (event: PointerEvent<HTMLElement>): void => {
        const active = findDrag(event)
        if (!active) {
            return
        }

        /** No button pressed anymore means the release was missed, so the drag is over, uncommitted */
        if (event.buttons === 0) {
            end()
            return
        }

        /** The panel's right edge stays put, so moving left widens it */
        const offset = active.startX - event.clientX
        if (active.moved === null && Math.abs(offset) < DRAG_DEAD_ZONE) {
            return
        }

        const { width, outletWidth } = active.start
        const wanted = width + offset
        const snapping = snaps && wanted > findSnapWidth(outletWidth)

        active.moved = { width: snapping ? outletWidth : clampWidth(wanted, findMaxWidth(outletWidth, snaps)), snapping }
        onDrag(active.moved)
    }

    const onPointerUp = (event: PointerEvent<HTMLElement>): void => {
        const active = findDrag(event)
        if (!active) {
            return
        }

        const { moved } = active
        end()

        if (moved?.snapping) {
            onExpand?.()
        } else if (moved) {
            onWidthChange(moved.width)
        }
    }

    /** A cancel, or a capture lost without a release, ends the drag uncommitted; after a release there's no drag left to end */
    const onInterrupt = (event: PointerEvent<HTMLElement>): void => {
        if (findDrag(event)) {
            end()
        }
    }

    const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return
        }

        if (event.key === 'Enter' || event.key === ' ') {
            /** The separator has no action, and nothing else should take one from it */
            event.preventDefault()
            return
        }

        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
            return
        }

        event.preventDefault()

        const { width, outletWidth } = measure(ref.current)
        const next = clampWidth(width + (event.key === 'ArrowLeft' ? KEY_STEP : -KEY_STEP), findMaxWidth(outletWidth, snaps))

        if (next !== Math.round(width)) {
            onWidthChange(next)
        }
    }

    const maxWidth = bounds ? findMaxWidth(bounds.outletWidth, snaps) : undefined
    /** While snapping, the panel shows the outlet's width, but the widest it would keep is the maximum */
    const value = drag ? (drag.snapping && maxWidth !== undefined ? maxWidth : drag.width) : bounds ? Math.round(bounds.width) : undefined

    return (
        <>
            <Box
                ref={ref}
                data-testid="record-panel-grip"
                role="separator"
                tabIndex={0}
                aria-orientation="vertical"
                aria-label={translator.formatTranslation(Translation.RECORD_PANEL_WIDTH)}
                aria-valuenow={value}
                aria-valuetext={value === undefined ? undefined : translator.formatTranslation(Translation.RECORD_PANEL_WIDTH_VALUE, { width: value })}
                aria-valuemin={MIN_WIDTH}
                aria-valuemax={maxWidth}
                sx={gripStyle(dragging)}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onInterrupt}
                onLostPointerCapture={onInterrupt}
                onDoubleClick={() => onWidthChange(null)}
                onKeyDown={onKeyDown}
                onFocus={remeasure}
            />
            {/** Mounted with the grip, so the text it gets while snapping is announced */}
            <Box data-testid="record-panel-snap-status" role="status" sx={visuallyHidden}>
                {drag?.snapping ? translator.formatTranslation(Translation.RECORD_PANEL_SNAP) : null}
            </Box>
        </>
    )
}
